/* eslint-disable */
import { useEffect, useRef, useState } from 'react';
import QrScanner from 'qr-scanner';
import QrFrame from '../../assets/qr-frame.svg';
import './QrStyles.css';
import { useTranslation } from 'react-i18next';
import { Button } from '../../shared/ui';
import { useNavigate } from 'react-router-dom';
import { SeoHead } from '../../components/SeoHead';
import Modal from '../../components/modal/Modal';
import { parseClientIdQrPayload, setClientId } from '../../hookServices/clientId';
import { resolveScannedQrPayload, scanQrPayloadFromImageFile } from '../../hookServices/processQrScan';
import ReactGA from 'react-ga';

function isAndroidDevice() {
  if (typeof navigator === 'undefined') {
    return false;
  }
  if (typeof window !== 'undefined' && window.Capacitor?.getPlatform?.() === 'android') {
    return true;
  }
  return /Android/i.test(navigator.userAgent);
}

/**
 * Centered square scan region (~40% of the smaller video dimension).
 */
function calculateCenterScanRegion(video) {
  const scanSize = Math.round(Math.min(video.videoWidth, video.videoHeight) * 0.4);
  return {
    x: Math.round((video.videoWidth - scanSize) / 2),
    y: Math.round((video.videoHeight - scanSize) / 2),
    width: scanSize,
    height: scanSize,
  };
}

const ScannerPage = () => {
  const scanner = useRef(null);
  const videoEl = useRef(null);
  const qrBoxEl = useRef(null);
  const fileInputRef = useRef(null);
  const [error, setError] = useState(null);
  const [cameraError, setCameraError] = useState(null);
  const [manualClientIdQr, setManualClientIdQr] = useState('');
  const [tokaErrorDetails, setTokaErrorDetails] = useState(null);
  const [energyCardAlreadyScanned, setEnergyCardAlreadyScanned] = useState(false);
  const [energyCardInstallOpen, setEnergyCardInstallOpen] = useState(false);
  const [isImportingFromPhoto, setIsImportingFromPhoto] = useState(false);
  const navigate = useNavigate();
  const [isScanSuccess, setIsScanSuccess] = useState(false);
  const [qrData, setQrData] = useState(null);

  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language || 'ua';

  useEffect(() => {
    window.scrollTo(0, 60);
  }, []);

  const onScanSuccess = qrResult => {
    setQrData(qrResult.data);
    setError(null);
    setIsScanSuccess(true);
  };

  const stopScanner = () => {
    scanner.current?.stop();
  };

  const applyResolvedQr = result => {
    if (result.type === 'redirect') {
      stopScanner();
      window.location.href = result.href;
      return;
    }
    if (result.type === 'navigate') {
      stopScanner();
      navigate(result.path);
      return;
    }
    if (result.type === 'energyCardAlreadyScanned') {
      stopScanner();
      setEnergyCardAlreadyScanned(true);
      return;
    }
    if (result.type === 'energyCardRequiresNativeApp') {
      stopScanner();
      setEnergyCardInstallOpen(true);
      return;
    }

    if (
      result.messageKey === 'qrScanner.tokaNotFound' ||
      result.messageKey === 'qrScanner.tokaLookupError' ||
      result.messageKey === 'qrScanner.invalidTokaUrl'
    ) {
      stopScanner();
      setTokaErrorDetails({ tokaNumber: result.tokaNumber || '', url: result.url || '' });
    }
    setError(t(result.messageKey || 'qrScanner.loadFromPhotoError'));
  };

  const closeEnergyCardInstallPopup = () => {
    setEnergyCardInstallOpen(false);
    setIsScanSuccess(false);
    setQrData(null);
    setError(null);
  };

  const handleEnergyCardInstallApp = () => {
    ReactGA.event({
      category: 'installApp',
      action: 'scanner energy card web install popup',
    });
    setEnergyCardInstallOpen(false);
    navigate('/install-app');
  };

  const handlePhotoQrImport = async event => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }

    setError(null);
    setTokaErrorDetails(null);
    setIsScanSuccess(false);
    setQrData(null);
    setIsImportingFromPhoto(true);

    try {
      const payload = await scanQrPayloadFromImageFile(file);
      if (!payload) {
        setError(t('qrScanner.loadFromPhotoError'));
        return;
      }

      stopScanner();
      const result = await resolveScannedQrPayload(payload);
      applyResolvedQr(result);
    } catch {
      setError(t('qrScanner.loadFromPhotoError'));
    } finally {
      setIsImportingFromPhoto(false);
    }
  };

  const importClientIdFromQr = value => {
    const importedClientId = parseClientIdQrPayload(value);
    if (!importedClientId) {
      setError(t('qrScanner.clientIdInvalid'));
      return false;
    }

    const didStore = setClientId(importedClientId);
    if (!didStore) {
      setError(t('qrScanner.clientIdInvalid'));
      return false;
    }

    stopScanner();
    window.location.href = '/history';
    return true;
  };

  const handleManualClientIdImport = () => {
    setError(null);
    importClientIdFromQr(manualClientIdQr.trim());
  };

  // iOS native scanner via Capacitor NativeBridge.
  // Android (browser + native app) uses the simple in-WebView scanner — native ScanActivity
  // callbacks are unreliable with Capacitor 6's Activity Result API migration.
  const hasNativeScan =
    typeof window !== 'undefined' && !isAndroidDevice() && Boolean(window.Capacitor?.Plugins?.NativeBridge?.scanQR);
  const useNativeScanner = !!hasNativeScan;
  const useSimpleWebScanner = isAndroidDevice();
  const NATIVE_QR_CALLBACK = '__onNativeQRResult';

  const callNativeScanQR = callbackName => {
    if (window.AndroidInterface?.scanQR) {
      window.AndroidInterface.scanQR(callbackName);
    } else if (window.Capacitor?.Plugins?.NativeBridge?.scanQR) {
      window.Capacitor.Plugins.NativeBridge.scanQR({ callbackName });
    }
  };

  // Track whether we've already auto-launched the native scanner once.
  // After the user grants camera permission and either scans or cancels, we don't want
  // to immediately re-trigger the scanner activity — that would create an infinite loop
  // when the activity returns RESULT_CANCELED and the page re-renders.
  const [autoLaunched, setAutoLaunched] = useState(false);

  useEffect(() => {
    if (!useNativeScanner) return;
    window[NATIVE_QR_CALLBACK] = data => {
      if (data) {
        setQrData(data);
        setIsScanSuccess(true);
      }
    };
    // Auto-launch the native scanner immediately on mount so the system camera
    // permission prompt appears right away on first entry, instead of forcing the
    // user to first see a placeholder screen and tap an extra button.
    if (!autoLaunched) {
      setAutoLaunched(true);
      callNativeScanQR(NATIVE_QR_CALLBACK);
    }
    return () => {
      delete window[NATIVE_QR_CALLBACK];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useNativeScanner]);

  useEffect(() => {
    if (!isScanSuccess || !qrData) {
      return;
    }

    let cancelled = false;
    (async () => {
      const result = await resolveScannedQrPayload(qrData);
      if (cancelled) return;
      applyResolvedQr(result);
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isScanSuccess, qrData]);

  useEffect(() => {
    if (useNativeScanner || tokaErrorDetails || energyCardAlreadyScanned || energyCardInstallOpen) {
      return undefined;
    }

    const video = videoEl.current;
    if (!video) {
      return undefined;
    }

    if (!window.isSecureContext) {
      setCameraError(t('qrScanner.cameraUnavailable'));
      return undefined;
    }

    let active = true;
    const scannerOptions = useSimpleWebScanner
      ? {
          onDecodeError: () => {},
          preferredCamera: 'environment',
          highlightScanRegion: true,
          highlightCodeOutline: true,
          overlay: qrBoxEl.current || undefined,
        }
      : {
          onDecodeError: () => {},
          preferredCamera: 'environment',
          highlightScanRegion: false,
          highlightCodeOutline: true,
          overlay: qrBoxEl.current || undefined,
          calculateScanRegion: calculateCenterScanRegion,
        };
    const scannerInstance = new QrScanner(video, onScanSuccess, scannerOptions);

    scanner.current = scannerInstance;

    scannerInstance
      .start()
      .then(async () => {
        if (!active) {
          return;
        }
        setCameraError(null);
        try {
          if (await scannerInstance.hasFlash()) {
            await scannerInstance.turnFlashOn();
          }
        } catch {
          // Desktop browsers and many front cameras have no flash.
        }
      })
      .catch(() => {
        if (active) {
          setCameraError(t('qrScanner.cameraUnavailable'));
        }
      });

    return () => {
      active = false;
      scannerInstance.stop();
      scannerInstance.destroy();
      scanner.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useNativeScanner, useSimpleWebScanner, tokaErrorDetails, energyCardAlreadyScanned, energyCardInstallOpen, t]);

  // SEO content for different languages - with competitor targeting (Toka, Ecofactor, Go2You)
  const seoContent = {
    ua: {
      title: 'Зарядити електромобіль | Мережа 220 км - Альтернатива Toka, Ecofactor, Go2You | 220-km.com',
      description:
        'Зарядити машину в мережі 220 просто та швидко! Мережа 220 км - найкраща альтернатива Toka, Ecofactor, Go2You. Роумінгова мережа зарядних станцій для електромобілів по всій Україні. Скануйте QR-код на станції, щоб зарядити машину миттєво.',
      keywords:
        'зарядити машину, мережа 220, зарядити електромобіль, тока зарядка, toka, екофактор, ecofactor, готую, go2you, зарядка електромобіля, де зарядити електромобіль, зарядка при відключенні світла, зарядка в блекаут, зарядка без світла, де є світло зарядка, де точно є світло, зарядка коли немає світла, QR код зарядка, швидка зарядка, Умань зарядка, Київ зарядка, Харків зарядка, Одеса зарядка',
      ogTitle: 'Зарядити машину в мережі 220 | Мережа 220 - зарядка авто',
      ogDescription:
        'Зарядити машину в мережі 220 просто! Мережа 220 - роумінгова мережа зарядних станцій. Скануйте QR-код на станції, щоб зарядити машину швидко та зручно.',
      twitterTitle: 'Зарядити машину | Мережа 220',
      twitterDescription:
        'Зарядити машину в мережі 220. Швидка зарядка електромобілів по всій Україні. QR-код для миттєвого старту.',
      structuredName: 'Зарядити машину - Мережа 220',
      structuredAlternateName: ['QR Scanner - Мережа 220', 'Зарядити машину в мережі 220', 'Зарядка авто мережа 220'],
      structuredDescription:
        'Зарядити машину в мережі 220. Швидке сканування QR-коду для заряджання електромобіля. Мережа 220 - роумінгова мережа зарядних станцій по всій Україні.',
      structuredOrgName: 'Мережа 220',
      structuredOrgAlternateName: ['220-km', 'Роумінгова мережа 220', 'Мережа зарядних станцій 220'],
      structuredOfferDescription: 'Заряджання електромобіля на станціях мережі 220. Зарядити машину швидко та зручно.',
      locale: 'uk_UA',
    },
    ru: {
      title: 'Зарядить электромобиль | Сеть 220 км - Альтернатива Toka, Ecofactor, Go2You | 220-km.com',
      description:
        'Зарядить машину в сети 220 просто и быстро! Сеть 220 км - лучшая альтернатива Toka, Ecofactor, Go2You. Роуминговая сеть зарядных станций для электромобилей по всей Украине. Сканируйте QR-код на станции, чтобы зарядить машину мгновенно.',
      keywords:
        'зарядить машину, сеть 220, зарядить электромобиль, тока зарядка, toka, экофактор, ecofactor, готуйо, go2you, зарядка электромобиля, где зарядить электромобиль, зарядка при отключении света, зарядка в блэкаут, зарядка без света, где есть свет зарядка, где точно есть свет, зарядка когда нет света, QR код зарядка, быстрая зарядка, Умань зарядка, Киев зарядка, Харьков зарядка, Одесса зарядка',
      ogTitle: 'Зарядить машину в сети 220 | Сеть 220 - зарядка авто',
      ogDescription:
        'Зарядить машину в сети 220 просто! Сеть 220 - роуминговая сеть зарядных станций. Сканируйте QR-код на станции, чтобы зарядить машину быстро и удобно.',
      twitterTitle: 'Зарядить машину | Сеть 220',
      twitterDescription:
        'Зарядить машину в сети 220. Быстрая зарядка электромобилей по всей Украине. QR-код для мгновенного старта.',
      structuredName: 'Зарядить машину - Сеть 220',
      structuredAlternateName: ['QR Scanner - Сеть 220', 'Зарядить машину в сети 220', 'Зарядка авто сеть 220'],
      structuredDescription:
        'Зарядить машину в сети 220. Быстрое сканирование QR-кода для зарядки электромобиля. Сеть 220 - роуминговая сеть зарядных станций по всей Украине.',
      structuredOrgName: 'Сеть 220',
      structuredOrgAlternateName: ['220-km', 'Роуминговая сеть 220', 'Сеть зарядных станций 220'],
      structuredOfferDescription: 'Зарядка электромобиля на станциях сети 220. Зарядить машину быстро и удобно.',
      locale: 'ru_RU',
    },
    en: {
      title: 'Charge Your EV | Network 220 km - Alternative to Toka, Ecofactor, Go2You | 220-km.com',
      description:
        'Charge your electric vehicle in Network 220 fast and easy! Network 220 km is the best alternative to Toka, Ecofactor, Go2You in Ukraine. Roaming network of EV charging stations. Scan QR code at the station to start charging instantly.',
      keywords:
        'charge EV, network 220, charge electric vehicle, toka charging, ecofactor, go2you, EV charging Ukraine, charging stations Ukraine, QR code charging, fast charging, EV charging during blackout, charging during power outage, where is electricity Ukraine, charging stations with generator, blackout EV charging, Kyiv charging, Kharkiv charging, Odesa charging, Lviv charging',
      ogTitle: 'Charge Your EV in Network 220 | Network 220 - EV Charging',
      ogDescription:
        'Charge your electric vehicle in Network 220 easily! Network 220 is a roaming EV charging network. Scan QR code at station to charge your car fast and conveniently.',
      twitterTitle: 'Charge Your EV | Network 220',
      twitterDescription:
        'Charge your electric vehicle in Network 220. Fast EV charging across Ukraine. QR code for instant start.',
      structuredName: 'Charge Your EV - Network 220',
      structuredAlternateName: ['QR Scanner - Network 220', 'Charge EV in Network 220', 'EV Charging Network 220'],
      structuredDescription:
        'Charge your electric vehicle in Network 220. Fast QR code scanning for EV charging. Network 220 is a roaming network of charging stations across Ukraine.',
      structuredOrgName: 'Network 220',
      structuredOrgAlternateName: ['220-km', 'Roaming Network 220', 'Charging Stations Network 220'],
      structuredOfferDescription:
        'Electric vehicle charging at Network 220 stations. Charge your car fast and conveniently.',
      locale: 'en_US',
    },
  };

  const currentSeo = seoContent[currentLanguage] || seoContent.ua;

  // Structured data for QR scanner page with multiple search phrase variations
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: currentSeo.structuredName,
    alternateName: currentSeo.structuredAlternateName,
    applicationCategory: 'UtilityApplication',
    operatingSystem: 'Any',
    description: currentSeo.structuredDescription,
    url: 'https://220-km.com/scanner',
    inLanguage: currentLanguage === 'ua' ? 'uk' : currentLanguage,
    provider: {
      '@type': 'Organization',
      name: currentSeo.structuredOrgName,
      alternateName: currentSeo.structuredOrgAlternateName,
      url: 'https://220-km.com',
    },
    offers: {
      '@type': 'Offer',
      description: currentSeo.structuredOfferDescription,
    },
  };

  return (
    <>
      <SeoHead fallbackTitle="220 km">
        <title>{currentSeo.title}</title>
        <meta name="description" content={currentSeo.description} />
        <meta name="keywords" content={currentSeo.keywords} />
        <link rel="canonical" href="https://220-km.com/scanner" />

        {/* Alternate language versions - hreflang tags */}
        <link rel="alternate" hreflang="uk" href="https://220-km.com/scanner?lang=ua" />
        <link rel="alternate" hreflang="ru" href="https://220-km.com/scanner?lang=ru" />
        <link rel="alternate" hreflang="en" href="https://220-km.com/scanner?lang=en" />
        <link rel="alternate" hreflang="x-default" href="https://220-km.com/scanner" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://220-km.com/scanner" />
        <meta property="og:title" content={currentSeo.ogTitle} />
        <meta property="og:description" content={currentSeo.ogDescription} />
        <meta property="og:image" content="https://220-km.com/logo512.png" />
        <meta property="og:locale" content={currentSeo.locale} />
        <meta property="og:locale:alternate" content="uk_UA" />
        <meta property="og:locale:alternate" content="ru_RU" />
        <meta property="og:locale:alternate" content="en_US" />
        <meta property="og:site_name" content={currentSeo.structuredOrgName} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://220-km.com/scanner" />
        <meta property="twitter:title" content={currentSeo.twitterTitle} />
        <meta property="twitter:description" content={currentSeo.twitterDescription} />
        <meta property="twitter:image" content="https://220-km.com/logo512.png" />

        {/* Additional SEO meta tags */}
        <meta name="robots" content="index, follow" />
        <meta
          name="language"
          content={currentLanguage === 'ua' ? 'Ukrainian' : currentLanguage === 'ru' ? 'Russian' : 'English'}
        />
        <meta name="revisit-after" content="7 days" />
        <meta name="author" content={currentSeo.structuredOrgName} />
        <html lang={currentLanguage === 'ua' ? 'uk' : currentLanguage} />

        {/* Mobile app related */}
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-title" content={currentSeo.structuredName} />

        {/* Structured Data */}
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </SeoHead>

      <div className={'qr-container'}>
        {energyCardAlreadyScanned ? (
          <div className="qr-ec-already-scanned" role="status">
            <p className="qr-ec-already-scanned__text">{t('qrScanner.energyCardAlreadyScanned')}</p>
            <Button
              onClick={() => navigate('/history')}
              variant="outline"
              style={{
                display: 'flex',
                gap: '12px',
                width: '86%',
                maxWidth: 360,
                justifyContent: 'center',
              }}
            >
              <span style={{ fontSize: '24px', fontWeight: '300', letterSpacing: '2px' }}>
                {t('qrScanner.backButton')}
              </span>
            </Button>
          </div>
        ) : tokaErrorDetails ? (
          <div
            className={'qr-reader'}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '40vh',
              padding: 24,
            }}
          >
            <p
              className="qr-toka-error"
              style={{
                color: '#ff6b6b',
                textAlign: 'center',
                padding: '16px',
                marginBottom: 24,
                fontSize: '16px',
                wordBreak: 'break-all',
              }}
            >
              {tokaErrorDetails.tokaNumber
                ? t('qrScanner.tokaErrorDetail', {
                    tokaNumber: tokaErrorDetails.tokaNumber,
                    url: tokaErrorDetails.url || '—',
                  })
                : t('qrScanner.tokaInvalidUrlDetail', {
                    url: tokaErrorDetails.url || '—',
                  })}
            </p>
            <Button
              onClick={() => navigate(-1)}
              variant="outline"
              style={{
                display: 'flex',
                gap: '12px',
                width: '86%',
                justifyContent: 'center',
              }}
            >
              <span style={{ fontSize: '24px', fontWeight: '300', letterSpacing: '2px' }}>
                {t('qrScanner.backButton')}
              </span>
            </Button>
          </div>
        ) : (
          <>
            <div className={`qr-reader${useSimpleWebScanner ? ' qr-reader--simple' : ''}`}>
              {useNativeScanner ? (
                // The native camera activity launches automatically on mount; this block
                // is only visible if the user cancels the scanner and returns to this
                // page. Keep it minimal — just a single retry button, no extra copy.
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 24,
                    minHeight: '40vh',
                  }}
                >
                  <Button
                    onClick={() => callNativeScanQR(NATIVE_QR_CALLBACK)}
                    variant="outline"
                    style={{ margin: '0 auto' }}
                  >
                    <span>{t('qrScanner.nameButton')}</span>
                  </Button>
                </div>
              ) : useSimpleWebScanner ? (
                <>
                  <video ref={videoEl} playsInline muted />
                  <div ref={qrBoxEl} className="qr-box">
                    <img src={QrFrame} alt="" width={256} height={256} className="qr-frame" aria-hidden="true" />
                  </div>
                </>
              ) : (
                <div className="qr-viewport">
                  <video ref={videoEl} playsInline muted />

                  <div className="qr-top-bar">
                    <button
                      type="button"
                      className="qr-icon-button"
                      onClick={() => navigate(-1)}
                      aria-label={t('qrScanner.backButton')}
                    >
                      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path
                          d="M15 6L9 12L15 18"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                    {!cameraError && <span className="qr-status-chip">{t('qrScanner.scanning')}</span>}
                  </div>

                  <div ref={qrBoxEl} className="qr-target" aria-hidden="true">
                    <div className="qr-target__grid" />
                    <span className="qr-target__corner qr-target__corner--tl" />
                    <span className="qr-target__corner qr-target__corner--tr" />
                    <span className="qr-target__corner qr-target__corner--bl" />
                    <span className="qr-target__corner qr-target__corner--br" />
                    <div className="qr-target__scanline" />
                  </div>

                  {!cameraError && <p className="qr-hint">{t('qrScanner.scanHint')}</p>}
                </div>
              )}
            </div>
            {error && !tokaErrorDetails && (
              <p
                className="qr-toka-error"
                style={{ color: '#ff6b6b', textAlign: 'center', padding: '8px 16px', margin: '0 16px' }}
              >
                {error}
              </p>
            )}
            {cameraError && !useNativeScanner && (
              <div className="qr-fallback-panel">
                <p>{cameraError}</p>
                <input
                  type="text"
                  value={manualClientIdQr}
                  onChange={event => setManualClientIdQr(event.target.value)}
                  placeholder={t('qrScanner.pasteClientIdHint')}
                />
                <Button
                  onClick={handleManualClientIdImport}
                  variant="outline"
                  style={{ width: '100%', justifyContent: 'center' }}
                >
                  <span>{t('qrScanner.importClientIdButton')}</span>
                </Button>
              </div>
            )}
            <div className={'button-container'}>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoQrImport}
                className="qr-hidden-file-input"
                aria-hidden="true"
                tabIndex={-1}
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                variant="outline"
                className="qr-gallery-button"
                disabled={isImportingFromPhoto}
              >
                <span>{isImportingFromPhoto ? t('qrScanner.loadingFromPhoto') : t('qrScanner.loadFromPhoto')}</span>
              </Button>
              <Button onClick={() => navigate(-1)} variant="outline" className="qr-back-button">
                <span>{t('qrScanner.backButton')}</span>
              </Button>
            </div>
          </>
        )}
      </div>

      {energyCardInstallOpen ? (
        <Modal setModalOpen={setEnergyCardInstallOpen} modalOpen={energyCardInstallOpen}>
          <div style={{ position: 'relative', textAlign: 'center', padding: '8px 4px 4px' }}>
            <button
              type="button"
              aria-label={t('close')}
              onClick={closeEnergyCardInstallPopup}
              style={{
                position: 'absolute',
                top: '-4px',
                right: '-4px',
                width: '48px',
                height: '48px',
                border: 'none',
                background: 'transparent',
                color: '#fff',
                fontSize: '40px',
                fontWeight: 300,
                lineHeight: 1,
                cursor: 'pointer',
                padding: 0,
                zIndex: 1,
              }}
            >
              ×
            </button>
            <p
              style={{
                fontSize: '18px',
                margin: '12px 0 24px',
                color: 'rgba(255, 255, 255, 0.85)',
                lineHeight: '1.5',
                padding: '0 40px',
              }}
            >
              {t('qrScanner.energyCardWebInstallPopup')}
            </p>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <Button
                onClick={handleEnergyCardInstallApp}
                style={{
                  display: 'flex',
                  width: '100%',
                  maxWidth: '100%',
                  margin: 0,
                  justifyContent: 'center',
                }}
              >
                <span style={{ fontSize: '22px', letterSpacing: '1px' }}>{t('installApp')}</span>
              </Button>
              <Button
                variant="outline-alt"
                onClick={closeEnergyCardInstallPopup}
                style={{
                  display: 'flex',
                  width: '100%',
                  maxWidth: '100%',
                  margin: 0,
                  justifyContent: 'center',
                }}
              >
                <span style={{ fontSize: '22px', letterSpacing: '1px' }}>{t('close')}</span>
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}
    </>
  );
};

export default ScannerPage;
